import React, { useCallback, useEffect, useState } from "react";
import { ApiService } from "../../services/api";
import { TercihLeadRow } from "../../types/tercih";

const LIMIT = 25;

const TercihLeads: React.FC = () => {
  const [leads, setLeads] = useState<TercihLeadRow[]>([]);
  const [toplam, setToplam] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [aramaInput, setAramaInput] = useState("");
  const [yukleniyor, setYukleniyor] = useState(false);
  const [detay, setDetay] = useState<TercihLeadRow | null>(null);

  const yukle = useCallback(async () => {
    setYukleniyor(true);
    try {
      const res = await ApiService.getTercihLeads({ page, limit: LIMIT, q });
      setLeads(res.leads);
      setToplam(res.toplam);
    } catch {
      setLeads([]);
    } finally {
      setYukleniyor(false);
    }
  }, [page, q]);

  useEffect(() => {
    yukle();
  }, [yukle]);

  const handleExport = async () => {
    try {
      const { blob, fileName } = await ApiService.exportTercihLeadsCsv();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName || "tercih-leadleri.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      /* sessiz */
    }
  };

  const sayfaSayisi = Math.max(1, Math.ceil(toplam / LIMIT));
  const tarih = (s: string) =>
    new Date(s).toLocaleString("tr-TR", { dateStyle: "medium", timeStyle: "short" });

  return (
    <div className="p-4 sm:p-6">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Tercih Robotu Lead'leri</h1>
          <p className="text-sm text-gray-500">
            Tercih robotundan bilgi bırakan öğrenciler ({toplam})
          </p>
        </div>
        <button
          onClick={handleExport}
          className="self-start rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          ⬇ CSV indir
        </button>
      </div>

      {/* Arama */}
      <div className="mb-4 flex gap-2">
        <input
          value={aramaInput}
          onChange={(e) => setAramaInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              setPage(1);
              setQ(aramaInput);
            }
          }}
          placeholder="Ad, e-posta veya telefon ara…"
          className="w-full max-w-sm rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-600 focus:outline-none"
        />
        <button
          onClick={() => {
            setPage(1);
            setQ(aramaInput);
          }}
          className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:border-blue-400"
        >
          Ara
        </button>
      </div>

      {/* Tablo */}
      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
            <tr>
              <th className="px-4 py-3">Ad Soyad</th>
              <th className="px-4 py-3">E-posta</th>
              <th className="px-4 py-3">Telefon</th>
              <th className="px-4 py-3">Puan</th>
              <th className="px-4 py-3">Sıra</th>
              <th className="px-4 py-3">Hedef</th>
              <th className="px-4 py-3">Tarih</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {yukleniyor ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-400">
                  Yükleniyor…
                </td>
              </tr>
            ) : leads.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-400">
                  Henüz lead yok.
                </td>
              </tr>
            ) : (
              leads.map((l) => (
                <tr
                  key={l.id}
                  onClick={() => setDetay(l)}
                  className="cursor-pointer hover:bg-blue-50/40"
                >
                  <td className="px-4 py-3 font-medium text-gray-900">{l.adSoyad}</td>
                  <td className="px-4 py-3 text-gray-600">{l.email || "—"}</td>
                  <td className="px-4 py-3 text-gray-600">{l.telefon || "—"}</td>
                  <td className="px-4 py-3 text-gray-600">{l.puanTuru || "—"}</td>
                  <td className="px-4 py-3 text-gray-600">
                    {l.basariSirasi ? l.basariSirasi.toLocaleString("tr-TR") : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                      {l.hedefSayisi} program
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-500">{tarih(l.createdAt)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Sayfalama */}
      {sayfaSayisi > 1 && (
        <div className="mt-4 flex items-center justify-center gap-2">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm disabled:opacity-40"
          >
            Önceki
          </button>
          <span className="text-sm text-gray-500">
            {page} / {sayfaSayisi}
          </span>
          <button
            disabled={page >= sayfaSayisi}
            onClick={() => setPage((p) => p + 1)}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm disabled:opacity-40"
          >
            Sonraki
          </button>
        </div>
      )}

      {/* Detay modal */}
      {detay && (
        <div
          onClick={() => setDetay(null)}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-5"
          >
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">{detay.adSoyad}</h3>
              <button onClick={() => setDetay(null)} className="text-gray-400 hover:text-gray-600">
                ✕
              </button>
            </div>
            <div className="space-y-1 text-sm text-gray-600">
              <div>E-posta: {detay.email || "—"}</div>
              <div>Telefon: {detay.telefon || "—"}</div>
              <div>
                Puan türü: {detay.puanTuru || "—"} · Sıra:{" "}
                {detay.basariSirasi ? detay.basariSirasi.toLocaleString("tr-TR") : "—"}
              </div>
              <div>Tarih: {tarih(detay.createdAt)}</div>
            </div>
            {detay.hedefler.length > 0 && (
              <div className="mt-4">
                <div className="mb-1 text-sm font-medium text-gray-700">
                  Hedef programlar ({detay.hedefler.length})
                </div>
                <ul className="space-y-1 text-sm text-gray-600">
                  {detay.hedefler.map((h, i) => (
                    <li key={i} className="rounded bg-gray-50 px-2 py-1">
                      {h.ad}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TercihLeads;
